import {
    keepPreviousData,
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
    type UseMutationResult,
    type UseQueryResult,
} from "@tanstack/react-query";
import {
    deleteCampaignTemplate,
    deleteEditorTemplate,
    fetchGlobalTemplates,
    fetchTrashedTemplates,
    fetchUserTemplates,
    permanentlyDeleteTemplate,
    restoreTrashedTemplate,
} from "./api";
import type {
    IGlobalTemplate,
    ITrashedTemplate,
    IUserTemplatesListParams,
    IUserTemplatesListResult,
} from "./types";

function cleanParams(p: IUserTemplatesListParams = { paginator: 0 }): IUserTemplatesListParams {
    const out: IUserTemplatesListParams = {};
    if ("paginator" in p) out.paginator = p.paginator as number;
    return Object.freeze(out);
}


export const templateKeys = {
    root: ["templates"] as const,
    global: () => [...templateKeys.root, "global"] as const,
    userList: (params: IUserTemplatesListParams = { paginator: 0 }) =>
        [...templateKeys.root, "user", cleanParams(params)] as const,
    trashList: () => [...templateKeys.root, "trash"] as const,
};

/** Global templates */
export const globalTemplatesQuery = queryOptions<IGlobalTemplate[]>({
    queryKey: templateKeys.global(),
    queryFn: fetchGlobalTemplates,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
});

export function useGlobalTemplates(): UseQueryResult<IGlobalTemplate[]> {
    return useQuery(globalTemplatesQuery);
}

/** User templates */
export const userTemplatesQuery = (params: IUserTemplatesListParams = { paginator: 0 }) =>
    queryOptions<IUserTemplatesListResult>({
        queryKey: templateKeys.userList(params),
        queryFn: () => fetchUserTemplates(params),
        placeholderData: keepPreviousData,
        staleTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
    });

export function useUserTemplates(
    params: IUserTemplatesListParams = { paginator: 0 },
    /** Lets a consumer defer the request (e.g. the command palette, which
     *  should not fetch until the user actually types a query). */
    opts?: { enabled?: boolean }
): UseQueryResult<IUserTemplatesListResult> {
    return useQuery({ ...userTemplatesQuery(params), enabled: opts?.enabled ?? true });
}

/** Delete template mutation (soft-delete → moves to trash) */
export function useDeleteTemplate(): UseMutationResult<
    any,
    Error,
    string | number,
    unknown
> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (templateId: string | number) => deleteCampaignTemplate(templateId),
        onSuccess: () => {
            // Invalidate user templates + trash list
            queryClient.invalidateQueries({ queryKey: templateKeys.root });
        },
    });
}

/** Delete editor template mutation (soft-delete → moves to trash via DELETE /template-editor-storage/{Tid}) */
export function useDeleteEditorTemplate(): UseMutationResult<
    any,
    Error,
    string | number,
    unknown
> {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (templateId: string | number) => deleteEditorTemplate(templateId),
        onSuccess: () => {
            // Invalidate user templates + trash list
            queryClient.invalidateQueries({ queryKey: templateKeys.root });
        },
    });
}

// ─── TRASH HOOKS ──────────────────────────────────────────────────────────

/** Query: List all trashed templates (GET /template-trash) */
export const trashTemplatesQuery = queryOptions<ITrashedTemplate[]>({
    queryKey: templateKeys.trashList(),
    queryFn: fetchTrashedTemplates,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
});

export function useTrashTemplates(): UseQueryResult<ITrashedTemplate[]> {
    return useQuery(trashTemplatesQuery);
}

/** Mutation: Restore a trashed template (PUT /template-trash/{Tid}) */
export function useRestoreTemplate(): UseMutationResult<
    any,
    Error,
    string | number,
    unknown
> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (templateId: string | number) => restoreTrashedTemplate(templateId),
        onSuccess: () => {
            // Refresh both the live template lists and the trash list
            queryClient.invalidateQueries({ queryKey: templateKeys.root });
        },
    });
}

/** Mutation: Permanently delete a template from trash (DELETE /template-trash/{Tid}) */
export function usePermanentlyDeleteTemplate(): UseMutationResult<
    any,
    Error,
    string | number,
    unknown
> {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (templateId: string | number) => permanentlyDeleteTemplate(templateId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: templateKeys.trashList() });
        },
    });
}